import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  Bell, CalendarBlank, CreditCard, Exam, Bus, ChatCircleText, Sparkle, ArrowUpRight, Check,
} from "@phosphor-icons/react";

const ICONS = { attendance: CalendarBlank, fee: CreditCard, exam: Exam, bus: Bus, message: ChatCircleText };
const GRADE_TREND = [
  { term: "T1", gpa: 7.8 }, { term: "T2", gpa: 8.1 }, { term: "T3", gpa: 8.4 },
  { term: "T4", gpa: 8.6 }, { term: "T5", gpa: 8.92 },
];

function timeAgo(iso) {
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

export default function ParentDashboard() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);

  useEffect(() => { api.get("/parent/feed").then(r => setFeed(r.data)); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="parent-dashboard">
      {/* Hero card for the child */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 bg-[hsl(244_53%_20%)] text-white rounded-md p-6 relative overflow-hidden">
          <div className="absolute inset-0 eos-grid-lines opacity-10"/>
          <div className="relative">
            <div className="eos-overline text-white/50">Parent · Aarav S. · B.Tech CSE Y2</div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mt-2 leading-tight">
              Hi {user?.name?.split(" ")[0] || "Parent"} — Aarav is on track.
            </h1>
            <p className="text-white/70 mt-2 text-sm">
              Attendance <b className="text-white">92.1%</b> · CGPA <b className="text-white">8.92</b> ·
              Fees <b className="text-emerald-300">paid</b> · Bus <b className="text-[hsl(20_90%_55%)]">B-14 · 6 min</b>.
            </p>
          </div>
        </div>

        <div className="lg:col-span-5 eos-card">
          <div className="eos-overline">Quick actions</div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <Button variant="outline" className="rounded-md justify-start"><CreditCard size={14} className="mr-1.5"/> Pay fees</Button>
            <Button variant="outline" className="rounded-md justify-start"><Bus size={14} className="mr-1.5"/> Track bus</Button>
            <Button variant="outline" className="rounded-md justify-start"><CalendarBlank size={14} className="mr-1.5"/> Request leave</Button>
            <Button variant="outline" className="rounded-md justify-start"><ChatCircleText size={14} className="mr-1.5"/> Message teacher</Button>
          </div>
          <div className="mt-4 pt-4 border-t border-border text-[11px] text-muted-foreground">
            Channels: <span className="text-emerald-700">Push · SMS · WhatsApp</span>
          </div>
        </div>
      </div>

      {/* Feed + Trend */}
      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Bell size={16} weight="duotone"/>
            <div className="font-heading font-semibold text-base">Live notifications</div>
          </div>
          <div className="divide-y divide-border">
            {feed.map(n => {
              const I = ICONS[n.type] || Bell;
              return (
                <div key={n.id} className="p-4 flex items-start gap-3 hover:bg-secondary/40">
                  <div className="w-9 h-9 rounded-md bg-secondary flex items-center justify-center">
                    <I size={16} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.time)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-5 eos-card">
          <div className="eos-overline">CGPA · 5-term trend</div>
          <div className="font-heading font-bold text-xl mt-1">Steady upward · +1.12 vs T1</div>
          <div className="h-48 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={GRADE_TREND}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
                <XAxis dataKey="term" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"/>
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" domain={[5, 10]}/>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12, border: "1px solid hsl(var(--border))" }}/>
                <Line type="monotone" dataKey="gpa" stroke="hsl(20 90% 48%)" strokeWidth={2.5} dot={{ r: 4 }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Fees + Exams */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="eos-card">
          <div className="eos-overline">Fees · This term</div>
          <div className="font-heading font-bold text-2xl mt-2 text-emerald-700 inline-flex items-center gap-2"><Check size={18} weight="bold"/>Paid in full</div>
          <p className="text-sm text-muted-foreground mt-1">₹1,84,400 settled on Jan 10 via UPI · GST e-invoice INV-26011.</p>
          <a href="/app/fees" className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">Download receipt <ArrowUpRight size={11} weight="bold"/></a>
        </div>
        <div className="eos-card">
          <div className="eos-overline">Next exam</div>
          <div className="font-heading font-bold text-2xl mt-2">CS301 Mid-Sem</div>
          <p className="text-sm text-muted-foreground mt-1">Feb 22 · 9:00 AM · Online · AI Proctoring enabled.</p>
          <a href="/app/exams" className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">View schedule <ArrowUpRight size={11} weight="bold"/></a>
        </div>
        <div className="eos-card">
          <div className="eos-overline">Transport · B-14</div>
          <div className="font-heading font-bold text-2xl mt-2">Arriving 6 min</div>
          <p className="text-sm text-muted-foreground mt-1">Whitefield → Campus · Stop B · Driver: R. Krishnan.</p>
          <a href="/app/campus" className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">Live tracker <ArrowUpRight size={11} weight="bold"/></a>
        </div>
      </div>

      <div className="eos-card flex items-center gap-4">
        <Sparkle size={20} weight="fill" className="text-[hsl(20_90%_48%)]"/>
        <div className="flex-1 text-sm">
          <b>Copilot summary</b>: Aarav's weekly performance is consistent. Mathematics shows a 4% dip vs last term — recommend
          encouraging the Linear Algebra peer-group study session this Friday.
        </div>
        <Button variant="outline" className="rounded-md">Open</Button>
      </div>
    </div>
  );
}
